import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { BookOpen, Play, CheckCircle2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';

interface Enrollment {
  id: string;
  progress_percentage: number;
  enrolled_at: string;
  courses: {
    id: string;
    title: string;
    description: string;
    thumbnail_url: string | null;
    profiles: {
      full_name: string;
    };
  };
}

export default function MyLearningPage() {
  const { profile } = useAuth();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile) {
      fetchEnrollments();
    }
  }, [profile]);

  const fetchEnrollments = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('enrollments')
      .select(`
        *,
        courses:course_id(
          id,
          title,
          description,
          thumbnail_url,
          profiles:teacher_id(full_name)
        )
      `)
      .eq('student_id', profile?.id)
      .order('enrolled_at', { ascending: false });

    if (data) {
      setEnrollments(data);
    }
    setLoading(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            I miei corsi
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Continua il tuo percorso di apprendimento
          </p>
        </div>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        ) : enrollments.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nessun corso acquistato
              </h3>
              <p className="text-gray-600 text-center mb-4">
                Inizia a imparare acquistando il tuo primo corso
              </p>
              <Link to="/dashboard/courses">
                <Button>Esplora corsi</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {enrollments.map((enrollment) => (
              <Card key={enrollment.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center relative">
                  {enrollment.courses.thumbnail_url ? (
                    <img
                      src={enrollment.courses.thumbnail_url}
                      alt={enrollment.courses.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <BookOpen className="w-16 h-16 text-white opacity-50" />
                  )}
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <Play className="w-16 h-16 text-white" />
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg line-clamp-2">
                    {enrollment.courses.title}
                  </CardTitle>
                  <CardDescription className="line-clamp-2">
                    {enrollment.courses.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Progresso</span>
                        <span className="font-semibold text-gray-900">
                          {enrollment.progress_percentage}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-blue-600 h-full transition-all duration-300"
                          style={{ width: `${enrollment.progress_percentage}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Docente: {enrollment.courses.profiles?.full_name}</span>
                      {enrollment.progress_percentage === 100 && (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    <Link to={`/dashboard/courses/${enrollment.courses.id}/learn`}>
                      <Button className="w-full">
                        {enrollment.progress_percentage === 0 ? 'Inizia corso' : 'Continua'}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
